<template>
    <div class="geo-reports">
        <check-for-missing-integrations :integrations="['Google analytics']">
            <div slot="no-integrations">
                <landing-page
                    image="/images/integrations/cta-messages/geo-report.png"
                    :headline="$t('geoReports.missingIntegration.googleAnalyticsLandingPage.headline')"
                    :description="$t('geoReports.missingIntegration.googleAnalyticsLandingPage.description')">
                    <hr />

                    <integration
                        service="analytics"
                        :border="true"
                        logo="/images/onboarding/google-analytics.svg"
                        :headline="$t('visitors.onboarding.analytics.headline')"
                        :description="$t('visitors.onboarding.analytics.description')"
                    />
                </landing-page>
            </div>

        <logo-loader v-if="!hasLoaded" :height="400" />

        <template v-else>
            <!-- KPI Cards -->
            <div class="kpi-row">
                <div class="kpi-card">
                    <div class="kpi-card__label">{{ $t('geoReports.sessions') }}</div>
                    <div class="kpi-card__value">{{ formatNumber(summary.sessions) }}</div>
                </div>

                <div class="kpi-card">
                    <div class="kpi-card__label">{{ $t('geoReports.engagedSessions') }}</div>
                    <div class="kpi-card__value">{{ formatNumber(summary.engagedSessions) }}</div>
                </div>

                <div class="kpi-card">
                    <div class="kpi-card__label">{{ $t('geoReports.conversions') }}</div>
                    <div class="kpi-card__value">{{ formatNumber(summary.conversions) }}</div>
                </div>
            </div>

            <!-- Sessions over time -->
            <div class="chart-panel">
                <div class="chart-panel__header">
                    <h5 class="chart-panel__title">{{ $t('geoReports.sessions') }}</h5>
                </div>
                <div class="sessions-chart"></div>
            </div>

            <!-- Treemap + Top pages side by side -->
            <div class="sessions-pages-row">
                <!-- AI channels treemap -->
                <div class="chart-panel">
                    <div class="chart-panel__header">
                        <h5 class="chart-panel__title">{{ $t('geoReports.treemapTitle') }}</h5>
                    </div>
                    <div class="channels-treemap"></div>

                    <p v-if="channelLegend.length" class="treemap-note">{{ $t('geoReports.treemapNote') }}</p>

                    <div v-if="channelLegend.length" class="treemap-legend">
                        <div
                            v-for="item in channelLegend"
                            :key="item.name"
                            class="treemap-legend__item"
                        >
                            <span class="treemap-legend__swatch" :style="{ background: item.color }"></span>
                            <span class="treemap-legend__label">{{ item.name }}</span>
                        </div>
                    </div>
                </div>

                <!-- Top pages list -->
                <div class="chart-panel">
                    <div class="chart-panel__header">
                        <h5 class="chart-panel__title">{{ $t('geoReports.pagesTitle') }}</h5>
                    </div>

                    <div class="pages-list" :class="{ 'pages-list--loading': pagesLoading }">
                        <div v-if="pagesLoading" class="pages-list__loader">
                            <logo-loader :height="120" />
                        </div>

                        <div class="pages-list__head">
                            <span class="pages-list__cell--page">{{ $t('geoReports.page') }}</span>
                            <span class="pages-list__cell--sessions pages-list__cell--sortable" :class="{ 'pages-list__cell--active': pagesSort === 'sessions' }" @click="onSort('sessions')">{{ $t('geoReports.sessions') }} <fa v-if="pagesSort === 'sessions'" icon="sort-down" class="pages-list__sort-icon" /></span>
                            <span class="pages-list__cell--engaged pages-list__cell--sortable" :class="{ 'pages-list__cell--active': pagesSort === 'engagedSessions' }" @click="onSort('engagedSessions')">{{ $t('geoReports.engagedSessions') }} <fa v-if="pagesSort === 'engagedSessions'" icon="sort-down" class="pages-list__sort-icon" /></span>
                            <span class="pages-list__cell--conversions pages-list__cell--sortable" :class="{ 'pages-list__cell--active': pagesSort === 'conversions' }" @click="onSort('conversions')">{{ $t('geoReports.conversions') }} <fa v-if="pagesSort === 'conversions'" icon="sort-down" class="pages-list__sort-icon" /></span>
                        </div>

                        <div
                            v-for="(row, index) in topPages.rows"
                            :key="index"
                            class="pages-list__row"
                        >
                            <span class="pages-list__cell--page" :title="row.page">{{ row.page }}</span>
                            <span class="pages-list__cell--sessions">{{ formatNumber(row.sessions) }}</span>
                            <span class="pages-list__cell--engaged">{{ formatNumber(row.engagedSessions) }}</span>
                            <span class="pages-list__cell--conversions">{{ formatNumber(row.conversions) }}</span>
                        </div>

                        <div v-if="topPages.rows.length === 0" class="pages-list__empty">
                            {{ $t('geoReports.noData') }}
                        </div>
                    </div>

                    <div class="pages-list__footer" v-if="topPages.total > pagesLimit">
                        <pagination-text
                            :total="topPages.total"
                            :limit="pagesLimit"
                            :page="pagesPage"
                        />
                        <pagination
                            :total="topPages.total"
                            :limit="pagesLimit"
                            :page="pagesPage"
                            @paginate="onPaginate"
                        />
                    </div>
                </div>
            </div>
            <!-- Yearly bar charts (2×2 grid) -->
            <div class="bar-charts-grid">
                <div class="chart-panel">
                    <div class="chart-panel__header">
                        <h5 class="chart-panel__title">{{ $t('geoReports.barChartSessions') }}</h5>
                    </div>
                    <div class="bar-chart bar-chart--sessions"></div>
                </div>

                <div class="chart-panel">
                    <div class="chart-panel__header">
                        <h5 class="chart-panel__title">{{ $t('geoReports.barChartEngagedSessions') }}</h5>
                    </div>
                    <div class="bar-chart bar-chart--engaged-sessions"></div>
                </div>

                <div class="chart-panel">
                    <div class="chart-panel__header">
                        <h5 class="chart-panel__title">{{ $t('geoReports.barChartEngagementRate') }}</h5>
                    </div>
                    <div class="bar-chart bar-chart--engagement-rate"></div>
                </div>

                <div class="chart-panel">
                    <div class="chart-panel__header">
                        <h5 class="chart-panel__title">{{ $t('geoReports.barChartKeyEvents') }}</h5>
                    </div>
                    <div class="bar-chart bar-chart--key-events"></div>
                </div>
            </div>
        </template>
        </check-for-missing-integrations>
    </div>
</template>

<script>
import { mapGetters } from 'vuex'
import LogoLoader from '@/app/shared/components/LogoLoader'
import Pagination from '@/app/layout/components/paginator/Pagination'
import PaginationText from '@/app/layout/components/paginator/PaginationText'
import CheckForMissingIntegrations from '@/app/integrations/components/CheckForMissingIntegrations'
import LandingPage from '@/app/onboarding/components/LandingPage'
import Integration from '@/app/onboarding/components/integrations/Integration'
import hasIntegration from '@/mixins/integrations/hasIntegration'
import { getSummary, getSessionsOverTime, getAiChannels, getTopPages, getYearlyBarCharts } from '@/services/geo-reports/GeoReportsService'

const HighCharts = require('highcharts')
require('highcharts/modules/treemap')(HighCharts)

function escapeHtml(str) {
    return String(str).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;')
}

const CHANNEL_COLORS = [
    { pattern: /chatgpt\.com/i,            color: '#3587ff' },
    { pattern: /perplexity/i,              color: '#ff6b6b' },
    { pattern: /edgepilot/i,               color: '#ffd93d' },
    { pattern: /edgeservices/i,            color: '#6bcb77' },
    { pattern: /copilot\.microsoft\.com/i, color: '#ff922b' },
    { pattern: /openai\.com/i,             color: '#cc5de8' },
    { pattern: /gemini\.google\.com/i,     color: '#20c997' },
    { pattern: /nimble\.ai/i,              color: '#f06595' },
    { pattern: /iask\.ai/i,                color: '#74c0fc' },
    { pattern: /claude\.ai/i,              color: '#a9e34b' },
    { pattern: /aitastic\.app/i,           color: '#e64980' },
    { pattern: /bnngpt\.com/i,             color: '#fd7e14' },
    { pattern: /writesonic\.com/i,         color: '#845ef7' },
    { pattern: /copy\.ai/i,                color: '#15aabf' },
    { pattern: /chat-gpt\.org/i,           color: '#37b24d' },
]
const FALLBACK_COLOR = '#adb5bd'

function resolveChannelColor(channel) {
    for (const { pattern, color } of CHANNEL_COLORS) {
        if (pattern.test(channel)) return color
    }
    return FALLBACK_COLOR
}

export default {
    name: 'GeoReports',

    components: {
        LogoLoader,
        Pagination,
        PaginationText,
        CheckForMissingIntegrations,
        LandingPage,
        Integration,
    },

    mixins: [hasIntegration],

    data() {
        return {
            hasLoaded: false,
            summary: {
                sessions: 0,
                engagedSessions: 0,
                conversions: 0,
            },
            topPages: {
                rows: [],
                total: 0,
            },
            pagesPage: 1,
            pagesLimit: 10,
            pagesSort: 'sessions',
            pagesLoading: false,
            sessionsChart: null,
            treemapChart: null,
            barCharts: [null, null, null, null],
            _sessionsData: null,
            _channelsData: null,
            _barChartsData: null,
            channelLegend: [],
        }
    },

    computed: {
        ...mapGetters('customer', {
            customer: 'getCustomer',
        }),

        ...mapGetters('datepicker', {
            start: 'getFrom',
            end: 'getTo',
        }),

        customerId() {
            return this.customer ? this.customer.id : null
        },

        fromDate() {
            return this.start.format('YYYY-MM-DD')
        },

        toDate() {
            return this.end.format('YYYY-MM-DD')
        },

        dateParams() {
            return { from: this.fromDate, to: this.toDate }
        },
    },

    watch: {
        start() { this.reload() },
        end() { this.reload() },

        hasLoaded(val) {
            if (val) {
                this.$nextTick(() => {
                    if (this._sessionsData) this.renderSessionsChart(this._sessionsData)
                    if (this._channelsData) this.renderTreemap(this._channelsData)
                    if (this._barChartsData) this.renderBarCharts(this._barChartsData)
                })
            }
        },
    },

    mounted() {
        this.load()
    },

    beforeDestroy() {
        if (this.sessionsChart) this.sessionsChart.destroy()
        if (this.treemapChart) this.treemapChart.destroy()
        this.barCharts.forEach(c => c && c.destroy())
    },

    methods: {
        reload() {
            if (this.sessionsChart) this.sessionsChart.destroy()
            if (this.treemapChart) this.treemapChart.destroy()
            this.sessionsChart = null
            this.treemapChart = null
            this.barCharts.forEach(c => c && c.destroy())
            this.barCharts = [null, null, null, null]
            this._sessionsData = null
            this._channelsData = null
            this._barChartsData = null
            this.channelLegend = []
            this.hasLoaded = false
            this.pagesPage = 1
            this.pagesSort = 'sessions'
            this.load()
        },

        load() {
            let pending = 5

            const done = () => {
                pending--
                if (pending === 0) this.hasLoaded = true
            }

            getSummary(this.customerId, this.dateParams, ({ data }) => {
                this.summary = {
                    sessions: (data && data.sessions) || 0,
                    engagedSessions: (data && data.engagedSessions) || 0,
                    conversions: (data && data.conversions) || 0,
                }
                done()
            }, () => done())

            getSessionsOverTime(this.customerId, this.dateParams, ({ data }) => {
                this._sessionsData = data
                done()
            }, () => done())

            getAiChannels(this.customerId, this.dateParams, ({ data }) => {
                this._channelsData = data
                done()
            }, () => done())

            getYearlyBarCharts(this.customerId, ({ data }) => {
                this._barChartsData = data
                done()
            }, () => done())

            this.loadTopPages(done)
        },

        loadTopPages(done) {
            if (!done) this.pagesLoading = true

            const params = {
                ...this.dateParams,
                page: this.pagesPage,
                limit: this.pagesLimit,
                sortBy: this.pagesSort,
            }

            getTopPages(this.customerId, params, ({ data }) => {
                this.topPages = {
                    rows: (data && data.rows) || [],
                    total: (data && data.total) || 0,
                }
                this.pagesLoading = false
                if (done) done()
            }, () => {
                this.pagesLoading = false
                if (done) done()
            })
        },

        onPaginate(page) {
            this.pagesPage = page
            this.loadTopPages()
        },

        onSort(sortBy) {
            if (this.pagesSort === sortBy) return
            this.pagesSort = sortBy
            this.pagesPage = 1
            this.loadTopPages()
        },

        renderSessionsChart(data) {
            const el = this.$el.querySelector('.sessions-chart')
            if (!el) return

            this.sessionsChart = HighCharts.chart(el, {
                chart: {
                    type: 'areaspline',
                    height: 240,
                    style: { fontFamily: '"Roboto","Helvetica Neue",Arial,sans-serif' },
                    backgroundColor: 'transparent',
                },
                title: { text: '' },
                credits: { enabled: false },
                xAxis: {
                    categories: data.labels || [],
                    tickInterval: Math.max(1, Math.ceil((data.labels || []).length / 8)),
                },
                yAxis: {
                    title: { text: '' },
                    min: 0,
                },
                tooltip: { shared: true },
                plotOptions: {
                    areaspline: {
                        fillOpacity: 0.12,
                        marker: { enabled: false },
                    },
                },
                legend: { enabled: false },
                series: [{
                    name: this.$t('geoReports.sessions'),
                    color: '#3587ff',
                    data: data.values || [],
                }],
            })
        },

        renderTreemap(data) {
            const el = this.$el.querySelector('.channels-treemap')
            if (!el) return

            const locale = this.$i18n.locale.replace('_', '-')
            const t = this.$t.bind(this)
            const rows = data.rows || []

            this.channelLegend = rows.map(r => ({
                name: r.channel,
                color: resolveChannelColor(r.channel),
            }))

            this.treemapChart = HighCharts.chart(el, {
                chart: {
                    height: 420,
                    style: { fontFamily: '"Roboto","Helvetica Neue",Arial,sans-serif' },
                    backgroundColor: 'transparent',
                },
                title: { text: '' },
                credits: { enabled: false },
                series: [{
                    type: 'treemap',
                    layoutAlgorithm: 'squarified',
                    data: rows.map(r => ({
                        name: r.channel,
                        value: Math.sqrt(r.sessions || 0),
                        color: resolveChannelColor(r.channel),
                        custom: { sessions: r.sessions },
                    })),
                    dataLabels: {
                        enabled: true,
                        style: { fontSize: '12px', fontWeight: '600', textOutline: 'none', color: '#fff', textAlign: 'center' },
                        formatter() {
                            const real = (this.point.options.custom && this.point.options.custom.sessions) || 0
                            return `<div style="text-align:center;line-height:1.3"><span style="font-size:20px;font-weight:700;opacity:0.95">${real.toLocaleString(locale)}</span><br/>${escapeHtml(this.point.name)}</div>`
                        },
                        useHTML: true,
                    },
                }],
                tooltip: {
                    pointFormatter() {
                        const real = (this.options.custom && this.options.custom.sessions) || 0
                        return `<b>${escapeHtml(this.name)}</b><br/>${t('geoReports.sessions')}: ${real}`
                    },
                },
            })

            this.treemapChart.reflow()
        },

        renderBarCharts(data) {
            const configs = [
                { selector: '.bar-chart--sessions',        name: this.$t('geoReports.barChartSessions'),        values: data.sessions,        color: '#3587ff', idx: 0, pct: false },
                { selector: '.bar-chart--engaged-sessions',name: this.$t('geoReports.barChartEngagedSessions'), values: data.engagedSessions, color: '#6bcb77', idx: 1, pct: false },
                { selector: '.bar-chart--engagement-rate', name: this.$t('geoReports.barChartEngagementRate'),  values: data.engagementRate,  color: '#ff922b', idx: 2, pct: true  },
                { selector: '.bar-chart--key-events',      name: this.$t('geoReports.barChartKeyEvents'),       values: data.keyEvents,       color: '#cc5de8', idx: 3, pct: false, details: data.keyEventDetails },
            ]

            configs.forEach(cfg => {
                const el = this.$el.querySelector(cfg.selector)
                if (!el) return
                this.barCharts[cfg.idx] = this.renderBarChart(el, cfg.name, data.labels, cfg.values, cfg.color, cfg.pct, cfg.details)
            })
        },

        renderBarChart(el, name, labels, values, color, isPct, keyEventDetails) {
            return HighCharts.chart(el, {
                chart: {
                    type: 'column',
                    height: 200,
                    style: { fontFamily: '"Roboto","Helvetica Neue",Arial,sans-serif' },
                    backgroundColor: 'transparent',
                },
                title: { text: '' },
                credits: { enabled: false },
                xAxis: {
                    categories: labels || [],
                    labels: { rotation: -45, style: { fontSize: '10px' } },
                },
                yAxis: {
                    title: { text: '' },
                    min: 0,
                    labels: {
                        formatter() {
                            return isPct ? `${this.value}%` : this.value
                        },
                    },
                },
                tooltip: {
                    pointFormatter() {
                        let text = `<span style="color:${color}">●</span> ${name}: <b>${isPct ? this.y + '%' : this.y}</b><br/>`
                        if (keyEventDetails && keyEventDetails.length) {
                            const idx = this.index
                            keyEventDetails.forEach(ev => {
                                const v = (ev.values && ev.values[idx]) || 0
                                if (v > 0) text += `&nbsp;&nbsp;<span style="color:#888">${ev.name}:</span> ${v}<br/>`
                            })
                        }
                        return text
                    },
                },
                legend: { enabled: false },
                plotOptions: {
                    column: {
                        borderRadius: 3,
                        color,
                        dataLabels: {
                            enabled: true,
                            inside: true,
                            verticalAlign: 'top',
                            y: -18,
                            style: {
                                fontSize: '10px',
                                fontWeight: '600',
                                color: '#555',
                                textOutline: 'none',
                            },
                            formatter() {
                                return isPct ? `${this.y}%` : this.y
                            },
                        },
                    },
                },
                series: [{ name, data: values || [] }],
            })
        },

        formatNumber(val) {
            if (val == null) return '—'
            return Number(val).toLocaleString(this.$i18n.locale.replace('_', '-'))
        },
    },
}
</script>

<style lang="scss" scoped>
.geo-reports {
    padding-bottom: 40px;

    .kpi-row {
        display: flex;
        gap: 16px;
        margin-bottom: 20px;
        flex-wrap: wrap;
    }

    .kpi-card {
        flex: 1 1 180px;
        background: #fff;
        border-radius: 6px;
        padding: 20px 22px;
        border: 1px solid #eee;

        &__label {
            font-size: 11px;
            font-weight: 600;
            color: #888;
            margin-bottom: 8px;
        }

        &__value {
            font-size: 28px;
            font-weight: 700;
            color: #1a1a2e;
            line-height: 1;
        }
    }

    .chart-panel {
        background: #fff;
        border: 1px solid #eee;
        border-radius: 6px;
        padding: 20px;
        margin-bottom: 20px;

        &__header {
            margin-bottom: 16px;
        }

        &__title {
            margin: 0;
            font-size: 13px;
            font-weight: 600;
            color: #333;
        }
    }

    .sessions-pages-row {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 20px;
        margin-bottom: 20px;

        .chart-panel {
            margin-bottom: 0;
        }
    }

    .bar-charts-grid {
        display: grid;
        grid-template-columns: 1fr;
        gap: 20px;
        margin-bottom: 20px;

        .chart-panel {
            margin-bottom: 0;
        }
    }

    .channels-treemap {
        width: 100%;
    }

    .treemap-note {
        font-size: 11px;
        color: #aaa;
        text-align: right;
        margin: 6px 0 0;
    }

    .treemap-legend {
        display: flex;
        flex-wrap: wrap;
        gap: 8px 20px;
        margin-top: 16px;
        padding-top: 14px;
        border-top: 1px solid #eee;

        &__item {
            display: flex;
            align-items: center;
            gap: 7px;
        }

        &__swatch {
            width: 12px;
            height: 12px;
            border-radius: 2px;
            flex-shrink: 0;
        }

        &__label {
            font-size: 12px;
            color: #555;
        }
    }

    .pages-list {
        position: relative;

        &--loading {
            pointer-events: none;

            .pages-list__head,
            .pages-list__row {
                opacity: 0.3;
            }
        }

        &__loader {
            position: absolute;
            inset: 0;
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 1;
        }

        &__head {
            display: flex;
            font-size: 11px;
            font-weight: 600;
            color: #888;
            padding-bottom: 8px;
            border-bottom: 1px solid #eee;
            margin-bottom: 4px;
        }

        &__cell--sortable {
            cursor: pointer;
            user-select: none;

            &:hover {
                color: #555;
            }
        }

        &__cell--active {
            color: #3587ff;
        }

        &__sort-icon {
            margin-left: 3px;
        }

        &__row {
            display: flex;
            align-items: center;
            padding: 10px 0;
            border-bottom: 1px solid #f5f5f5;
            font-size: 12px;

            &:last-child {
                border-bottom: none;
            }
        }

        &__empty {
            text-align: center;
            color: #aaa;
            padding: 30px 0;
            font-size: 12px;
        }

        &__cell--page {
            flex: 1 1 auto;
            color: #333;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
            padding-right: 16px;
        }

        &__cell--sessions,
        &__cell--engaged,
        &__cell--conversions {
            width: 120px;
            text-align: right;
            color: #555;
            flex-shrink: 0;
        }

        &__footer {
            display: flex;
            align-items: center;
            justify-content: space-between;
            padding-top: 12px;
            border-top: 1px solid #eee;
            margin-top: 8px;
        }
    }
}
</style>
